import {
  Card,
  Fab,
  Grid,
  Hidden,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { AttachMoney, AvTimer, Delete, Schedule, Update } from '@material-ui/icons';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ENTRY_TYPE_DRAFT,
  ENTRY_TYPE_EXPENSE,
  ENTRY_TYPE_SERVICE,
} from '../../config/staticValues';
import { deleteEntry, setEntry } from '../../redux/entry';
import { MoneyOffIcon } from '../../components/ThemedIcons';
import dateUtil from '../../util/dateUtil';
import { formatDollars } from '../../util/helperFunctions';
import { setMessage } from '../../redux/message';
import ConfirmDialog from '../../components/ConfirmDialog';
import Visible from '../../components/Visible';
import { taskHsl } from '../../components/Flag';
import { playNow } from '../../redux/draft';

const useStyles = makeStyles((theme) => ({
  draftItem: {
    backgroundColor: 'lightyellow',
  },
  entryItem: {},
}));

const EntryRow = ({ entry, disabled }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const startTime = dateUtil.stringToTimeFormat(entry.startTime, 'h:mm a');
  const endTime = dateUtil.stringToTimeFormat(entry.endTime, 'h:mm a');
  const isRunning = entry.status === 'RUNNING';
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const draftId = useSelector((state) => state.draft.id);
  const timecardId = useSelector((state) => state.timecard?.timecardId);

  var marker = { color: undefined, message: '' };
  switch (entry.type.toUpperCase()) {
    case ENTRY_TYPE_EXPENSE:
      if (entry.noCharge && entry.payBonus)
        marker = { color: 'error', message: 'No charge expense' };
      else if (entry.noCharge && !entry.payBonus)
        marker = { color: 'disabled', message: 'No charge/non-reimbursed expense' };
      else if (!entry.noCharge && !entry.payBonus)
        marker = { color: 'primary', message: 'Chargable/non-reimbursed expense' };
      else marker = { color: 'action', message: 'Expense' };
      break;
    case ENTRY_TYPE_SERVICE:
      if (entry.noCharge && entry.payBonus)
        marker = { color: 'error', message: 'No charge time entry' };
      else if (entry.noCharge && !entry.payBonus)
        marker = { color: 'disabled', message: 'No charge/no bonus time entry' };
      else if (!entry.noCharge && !entry.payBonus)
        marker = { color: 'primary', message: 'Billed/no bonus time entry' };
      else marker = { color: 'action', message: 'Time entry' };
      break;
    case ENTRY_TYPE_DRAFT:
      if (entry.noCharge && entry.payBonus)
        marker = { color: 'error', message: 'No charge draft entry' };
      else if (entry.noCharge && !entry.payBonus)
        marker = { color: 'disabled', message: 'No charge/no bonus draft entry' };
      else if (!entry.noCharge && !entry.payBonus)
        marker = { color: 'primary', message: 'Billed/no bonus draft entry' };
      else marker = { color: 'action', message: 'Draft entry' };
      break;
  }

  const onRowClick = (event) => {
    if (!isRunning) dispatch(setEntry(entry));
    else
      dispatch(
        setMessage(
          'This draft is currently running in the action pane. Make updates there.'
        )
      );
  };

  const onDeleteClick = (event) => {
    event.stopPropagation();
    setConfirmDialogOpen(true);
  };

  return (
    <>
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Are you sure you want to delete this entry?"
        message="This cannot be undone"
        onYes={() => {
          setConfirmDialogOpen(false);
          dispatch(deleteEntry({ id: entry.id, type: entry.type }));
        }}
        onNo={() => {
          setConfirmDialogOpen(false);
        }}
        yesCaption="Delete entry"
        noCaption="Cancel"
      />
      <Visible smUp>
        <Card
          className={
            entry.type == ENTRY_TYPE_DRAFT ? classes.draftItem : classes.entryItem
          }
          style={{
            marginBottom: 4,
            cursor: 'pointer',
            display: 'flex',
          }}
          onClick={onRowClick}
        >
          <Grid container style={{ padding: 4 }}>
            <Grid
              item
              style={{
                display: 'flex',
                width: 100,
                textAlign: 'right',
                paddingRight: 2,
                alignSelf: 'center',
              }}
            >
              <Tooltip title={marker.message}>
                {entry.type === ENTRY_TYPE_EXPENSE ? (
                  <AttachMoney color={marker.color} />
                ) : entry.type === ENTRY_TYPE_DRAFT ? (
                  <AvTimer color={marker.color} />
                ) : (
                  <Schedule color={marker.color} />
                )}
              </Tooltip>
              {entry.type === ENTRY_TYPE_EXPENSE ? (
                <Typography>
                  {Number.parseFloat(entry.expenseAmount).toFixed(2)}
                </Typography>
              ) : (
                <Typography>
                  &nbsp;{Number.parseFloat(entry.hours).toFixed(2)} hrs
                </Typography>
              )}
            </Grid>
            <Grid
              item
              style={{
                width: 84,
                paddingRight: 12,
                textAlign: 'right',
                alignSelf: 'center',
              }}
            >
              <Typography>{startTime}</Typography>
              <Typography>{endTime}</Typography>
            </Grid>
            <Grid item style={{ width: 'calc(100% - 184px)' }}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      position: 'absolute',
                      marginTop: 0,
                      marginRight: 4,
                    }}
                  >
                    {!disabled && !isRunning && !draftId && (
                      <Tooltip title="Resume this task with the same description">
                        <Fab
                          size="small"
                          aria-label="resume"
                          className="hoverShow"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(
                              playNow(
                                {
                                  companyName: entry.companyName,
                                  companyShortName: entry.companyShortName,
                                  incidentId: entry.incidentId,
                                  incidentName: entry.incidentName,
                                  taskId: entry.taskId,
                                  taskNumber: entry.taskNumber,
                                  taskName: entry.taskName,
                                  noCharge: entry.noCharge,
                                  payBonus: entry.payBonus,
                                  description: entry.description,
                                },
                                timecardId,
                                'resume'
                              )
                            );
                          }}
                        >
                          <Update color="primary" size="small" />
                        </Fab>
                      </Tooltip>
                    )}
                    {!disabled && !isRunning && (
                      <Fab
                        size="small"
                        aria-label="add"
                        className="hoverShow"
                        onClick={onDeleteClick}
                        style={{ marginLeft: 4 }}
                      >
                        <Delete color="error" size="small" />
                      </Fab>
                    )}
                  </div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip
                    title={
                      <div style={{ fontSize: 14, lineHeight: '1.5' }}>
                        {entry.companyName} * <span>{entry.id}</span> *&nbsp;
                        {entry.incidentName} ({entry.incidentId}) *&nbsp;{entry.taskName}
                      </div>
                    }
                  >
                    <Typography variant="caption" style={{ lineHeight: '2' }}>
                      <span style={{ color: taskHsl(entry) }}>{entry.companyName}</span> *{' '}
                      <span>{entry.id}</span> *&nbsp;
                      {entry.incidentName} ({entry.incidentId}) *&nbsp;{entry.taskName}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <Typography style={{ lineHeight: '1.4' }}>
                    {entry.description}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Visible>
      <Visible xsDown>
        <Card
          className={
            entry.type == ENTRY_TYPE_DRAFT ? classes.draftItem : classes.entryItem
          }
          onClick={onRowClick}
          style={{
            cursor: 'pointer',
            padding: 4,
            marginBottom: 12,
          }}
        >
          <Grid container>
            <Grid item xs={5} style={{ display: 'flex' }}>
              <Tooltip title={marker.message}>
                {entry.type === ENTRY_TYPE_EXPENSE ? (
                  <AttachMoney color={marker.color} />
                ) : entry.type === ENTRY_TYPE_DRAFT ? (
                  <AvTimer color={marker.color} />
                ) : (
                  <Schedule color={marker.color} />
                )}
              </Tooltip>
              {entry.type === ENTRY_TYPE_EXPENSE ? (
                <Typography>
                  {Number.parseFloat(entry.expenseAmount).toFixed(2)}
                </Typography>
              ) : (
                <Typography>
                  &nbsp;{Number.parseFloat(entry.hours).toFixed(2)} hrs
                </Typography>
              )}
            </Grid>
            <Grid item xs={7} style={{ textAlign: 'right' }}>
              <Typography>
                {startTime} - {endTime}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography color="textSecondary">
                {entry.companyShortName}: {entry.incidentName}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography color="textSecondary">{entry.taskName}</Typography>
            </Grid>
            <Grid item xs={11}>
              <Typography variant="subtitle2">{entry.description}</Typography>
            </Grid>
            <Grid item xs={1}>
              {!disabled && !isRunning && (
                <Delete color="error" size="small" onClick={onDeleteClick} />
              )}
            </Grid>
          </Grid>
        </Card>
      </Visible>
    </>
  );
};

export default EntryRow;
